import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getProviderBatch,
  getProviderBatchItems,
  listProviderBatches,
} from '../../api/providerApi';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { DataTable } from '../../components/ui/DataTable';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button, buttonBase, buttonVariants } from '../../components/ui/Button';
import { IconSearch } from '../../components/ui/icons';
import {
  PACKAGE_STATUS_OPTIONS,
  authorityStateLabel,
  batchDisplayId,
  candidateDetailPath,
  nextActionLabel,
  packageStatusLabel,
} from './providerReviewLabels';

export function IntakeExplorerBatchPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [batchItems, setBatchItems] = useState([]);
  const [panelError, setPanelError] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInputValue.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInputValue]);

  useEffect(() => {
    let cancelled = false;

    async function loadBatches() {
      setLoading(true);
      setError('');
      try {
        const response = await listProviderBatches({
          page,
          pageSize,
          search: search || undefined,
          status: statusFilter || undefined,
        });
        if (cancelled) return;
        const nextItems = response.items || [];
        setItems(nextItems);
        setTotal(response.meta?.total || 0);
        setSelectedId((current) => {
          if (nextItems.some((row) => row.batch_id === current)) return current;
          return nextItems[0]?.batch_id ?? null;
        });
      } catch (requestError) {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setSelectedId(null);
        setError(requestError.message || 'Unable to load intake batches.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadBatches();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setBatchItems([]);
      setPanelError('');
      return undefined;
    }

    let cancelled = false;

    async function loadPanel() {
      setPanelError('');
      try {
        const [detailResponse, itemRows] = await Promise.all([
          getProviderBatch(selectedId),
          getProviderBatchItems(selectedId),
        ]);
        if (cancelled) return;
        setDetail(detailResponse);
        setBatchItems(itemRows || []);
      } catch (requestError) {
        if (cancelled) return;
        setDetail(null);
        setBatchItems([]);
        setPanelError(requestError.message || 'Unable to load batch detail.');
      }
    }

    loadPanel();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const selected = items.find((row) => row.batch_id === selectedId) ?? null;
  const firstAsset = batchItems[0] || null;

  const columns = useMemo(
    () => [
      {
        key: 'batch',
        header: 'Batch',
        render: (row) => (
          <span className="font-semibold text-eorig-ink">{batchDisplayId(row)}</span>
        ),
      },
      {
        key: 'submitter_org',
        header: 'Submitter',
        render: (row) => row.submitter_org || '—',
      },
      {
        key: 'program',
        header: 'Program',
        render: (row) => row.program || '—',
      },
      {
        key: 'items_count',
        header: 'Items',
        render: (row) => (
          <span className="tabular-nums">{row.items_count ?? 0}</span>
        ),
      },
      {
        key: 'package_status',
        header: 'Package',
        render: (row) => (
          <StatusPill value={row.package_status} label={packageStatusLabel(row.package_status)} />
        ),
      },
      {
        key: 'authority_status',
        header: 'Authority',
        render: (row) => (
          <StatusPill
            value={row.authority_status}
            label={authorityStateLabel(row.authority_status)}
          />
        ),
      },
      {
        key: 'current_owner',
        header: 'Owner',
        render: (row) => <StatusPill value={row.current_owner} />,
      },
      {
        key: 'next_action',
        header: 'Next action',
        render: (row) => (
          <StatusPill
            value={row.next_action}
            tone={
              row.next_action === 'AWAITING_PROVIDER_REVIEW' ? 'warning' : 'info'
            }
            label={nextActionLabel(row.next_action)}
          />
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <section className="overflow-x-auto rounded-lg border border-[#E6EAF0] bg-white px-4 py-3">
        <div className="flex w-max min-w-full flex-nowrap items-center gap-2">
        <FilterTabs
          tabs={PACKAGE_STATUS_OPTIONS}
          value={statusFilter}
          ariaLabel="Filter by package status"
          onChange={(value) => {
            setPage(1);
            setStatusFilter(value);
          }}
        />
        <div className="relative w-64 shrink-0">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5A5A]">
            <IconSearch className="h-4 w-4" />
          </span>
          <input
            type="search"
            className="h-9 w-full rounded-md border border-[#D1D5DB] bg-white py-0 pl-9 pr-3 text-sm text-[#1F2933] placeholder:text-[#8D8D8D] focus:border-[#00A6D6] focus:outline-none focus:ring-2 focus:ring-[#00A6D6]/30"
            placeholder="reference / program / submitter"
            aria-label="Search batches"
            value={searchInputValue}
            onChange={(event) => setSearchInputValue(event.target.value)}
          />
        </div>
        </div>
      </section>

      {error ? (
        <ErrorBanner
          message={error}
          onRetry={() => {
            setPage(1);
            setSearch(searchInputValue.trim());
          }}
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_min(20rem,100%)] lg:items-start">
        <div className="min-w-0 space-y-3">
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.batch_id}
            onRowClick={(row) => setSelectedId(row.batch_id)}
            isSelected={(row) => row.batch_id === selectedId}
            loading={loading}
            emptyMessage="No intake batches found."
            minWidth={900}
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>

        <aside className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
          <h2 className="text-sm font-semibold text-eorig-ink">Selected batch</h2>
          {selected ? (
            <>
              {panelError ? (
                <p className="mt-3 text-sm text-rose-600" role="alert">
                  {panelError}
                </p>
              ) : null}
              <dl className="mt-4 space-y-3 text-sm">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Current stage
                  </dt>
                  <dd className="mt-0.5 text-eorig-ink">
                    {nextActionLabel(detail?.next_action || selected.next_action)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Items
                  </dt>
                  <dd className="mt-0.5 tabular-nums text-eorig-ink">
                    {detail?.items_count ?? selected.items_count ?? 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Exceptions
                  </dt>
                  <dd className="mt-0.5 text-eorig-ink">
                    {detail
                      ? `${detail.open_exceptions} open`
                      : panelError
                        ? '—'
                        : 'Loading…'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Evidence
                  </dt>
                  <dd className="mt-0.5 text-eorig-ink">
                    {detail
                      ? `${detail.evidence_satisfied}/${detail.evidence_total}`
                      : panelError
                        ? '—'
                        : 'Loading…'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Asset
                  </dt>
                  <dd className="mt-0.5 text-eorig-ink">
                    {firstAsset
                      ? firstAsset.asset_number
                      : panelError
                        ? '—'
                        : selected.items_count
                          ? 'Loading…'
                          : 'None yet'}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex flex-col gap-2">
                {firstAsset ? (
                  <Link
                    to={candidateDetailPath(firstAsset.id)}
                    className={`${buttonBase} ${buttonVariants.navy} w-full`}
                  >
                    Open eligibility review
                  </Link>
                ) : (
                  <Button type="button" variant="navy" disabled>
                    Open eligibility review
                  </Button>
                )}
              </div>
            </>
          ) : (
            <p className="mt-4 text-sm text-eorig-muted">
              Select a batch in the table to see summary metadata and actions.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
